import { useState, useEffect, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import {
  Grid3X3, AlertTriangle, DollarSign, ClipboardList, FileText, Ban,
} from "lucide-react";

import ReadinessBanner from "./improve/ReadinessBanner";
import SolutionsTab from "./improve/SolutionsTab";
import FmeaTab from "./improve/FmeaTab";
import CostBenefitTab from "./improve/CostBenefitTab";
import PilotPlanTab from "./improve/PilotPlanTab";
import PilotResultsTab from "./improve/PilotResultsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImprovePhase({ projectId, token }) {
  const [loading, setLoading] = useState(true);

  // Decision readiness state
  const [readiness, setReadiness] = useState(null);

  // Pilot plan state
  const [pilotPlan, setPilotPlan] = useState({
    hypothesis: "", test_design: "", success_metrics: "",
    duration: "", sample_size_target: "", rollback_plan: "",
  });

  // Pilot results state
  const [pilotResults, setPilotResults] = useState({
    before_metrics: "", after_metrics: "", test_conclusion: "",
    p_value: "", effect_size: "", learnings: "",
  });

  // Solutions state
  const [solutions, setSolutions] = useState([
    { solution_id: "", name: "", description: "", impact: "medium", effort: "medium", cost: "", timeline: "", owner: "", status: "proposed" }
  ]);

  // FMEA state
  const [fmea, setFmea] = useState({
    process_name: "",
    items: [{ failure_mode: "", effect: "", cause: "", severity: 5, occurrence: 5, detection: 5, recommended_action: "", responsible: "", status: "identified" }]
  });

  // Cost-benefit state
  const [costBenefit, setCostBenefit] = useState([
    { solution_name: "", implementation_cost: "", annual_benefit: "", one_time_benefit: "", notes: "" }
  ]);

  const fetchData = useCallback(async () => {
    try {
      const [improveRes, readinessRes, pilotPlanRes, pilotResultsRes] = await Promise.all([
        fetch(`${API}/projects/${projectId}/improve`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/projects/${projectId}/decision-readiness`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/projects/${projectId}/improve/pilot-plan`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/projects/${projectId}/improve/pilot-results`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (improveRes.ok) {
        const data = await improveRes.json();
        if (data.solutions?.length) setSolutions(data.solutions);
        if (data.fmea) setFmea(data.fmea);
        if (data.cost_benefit?.length) setCostBenefit(data.cost_benefit);
      }
      if (readinessRes.ok) setReadiness(await readinessRes.json());
      if (pilotPlanRes.ok) {
        const ppData = await pilotPlanRes.json();
        if (ppData.pilot_plan) setPilotPlan(ppData.pilot_plan);
      }
      if (pilotResultsRes.ok) {
        const prData = await pilotResultsRes.json();
        if (prData.pilot_results) setPilotResults(prData.pilot_results);
      }
    } catch (error) {
      console.error("Failed to fetch improve data:", error);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const drStatus = readiness?.status || "unknown";
  const drScore = readiness?.score ?? 0;
  const isBlocked = drStatus === "blocked";
  const requiresPilot = drStatus === "pilot_required" || drStatus === "exploratory_only";

  if (loading) {
    return <div className="text-muted-foreground">Loading Improve phase...</div>;
  }

  return (
    <div className="space-y-6" data-testid="improve-phase">
      <ReadinessBanner status={drStatus} score={drScore} readiness={readiness} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Improve Phase</h1>
          <p className="text-muted-foreground">Develop, evaluate, and prioritize solutions</p>
        </div>
      </div>

      {isBlocked && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-6 text-center" data-testid="improve-blocked-view">
          <Ban className="w-10 h-10 mx-auto mb-3 text-destructive" />
          <h3 className="font-heading font-semibold text-destructive mb-2">Improve Phase Blocked</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Decision readiness is <strong>blocked</strong>. You must return to Measure or Analyze to resolve the issues listed below before entering the Improve phase.
          </p>
          {readiness?.required_actions?.length > 0 && (
            <ul className="text-sm text-left max-w-lg mx-auto space-y-1">
              {readiness.required_actions.map((action, i) => (
                <li key={i} className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-destructive shrink-0" />
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!isBlocked && (
        <Tabs defaultValue="solutions" className="space-y-6">
          <TabsList className="bg-card border border-border flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="solutions" className="gap-2" data-testid="tab-solutions">
              <Grid3X3 className="w-4 h-4" /> Impact vs Effort
            </TabsTrigger>
            <TabsTrigger value="fmea" className="gap-2" data-testid="tab-fmea">
              <AlertTriangle className="w-4 h-4" /> FMEA
            </TabsTrigger>
            <TabsTrigger value="cost-benefit" className="gap-2" data-testid="tab-cost-benefit">
              <DollarSign className="w-4 h-4" /> Cost-Benefit
            </TabsTrigger>
            {requiresPilot && (
              <>
                <TabsTrigger value="pilot-plan" className="gap-2" data-testid="tab-pilot-plan">
                  <ClipboardList className="w-4 h-4" /> Pilot Plan
                </TabsTrigger>
                <TabsTrigger value="pilot-results" className="gap-2" data-testid="tab-pilot-results">
                  <FileText className="w-4 h-4" /> Pilot Results
                </TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="solutions" className="space-y-6">
            <SolutionsTab projectId={projectId} token={token} solutions={solutions} setSolutions={setSolutions} drStatus={drStatus} />
          </TabsContent>

          <TabsContent value="fmea" className="space-y-6">
            <FmeaTab projectId={projectId} token={token} fmea={fmea} setFmea={setFmea} />
          </TabsContent>

          <TabsContent value="cost-benefit" className="space-y-6">
            <CostBenefitTab projectId={projectId} token={token} costBenefit={costBenefit} setCostBenefit={setCostBenefit} />
          </TabsContent>

          {requiresPilot && (
            <TabsContent value="pilot-plan" className="space-y-6">
              <PilotPlanTab projectId={projectId} token={token} pilotPlan={pilotPlan} setPilotPlan={setPilotPlan} />
            </TabsContent>
          )}

          {requiresPilot && (
            <TabsContent value="pilot-results" className="space-y-6">
              <PilotResultsTab projectId={projectId} token={token} pilotResults={pilotResults} setPilotResults={setPilotResults} />
            </TabsContent>
          )}
        </Tabs>
      )}
    </div>
  );
}
